import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Code, Edit, Trash2, HardDrive, AlertTriangle, Bot, Upload, HelpCircle, Volume2, Truck, Plus } from "lucide-react";
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { showError, showSuccess } from '@/utils/toast';
import { GameRequestSummary } from '@/utils/gameRequests';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { useDownloadSettings } from '@/hooks/useDownloadSettings';
import EditDownloadForm from '@/components/EditDownloadForm';
import UploadTeleportModal from '@/components/UploadTeleportModal';
import { Input } from "@/components/ui/input";
import ImageGallery from '@/components/ImageGallery'; // Importáljuk az új komponenst
import GameUpdateRequestPanel from '@/components/GameUpdateRequestPanel';
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import DOMPurify from 'dompurify';
import { formatBytes } from '@/utils/format-bytes'; // Import formatBytes
import { Checkbox } from "@/components/ui/checkbox";

// Interfészek a GameDownloadsTab.tsx-ből
interface Program {
    id: number;
    program_name: string;
}

interface Translator {
    id: number;
    slug: string;
    name: string;
    supportLinks?: SupportLink[];
    avatar?: string;
}

export interface SupportLink {
    type: 'paypal' | 'buymeacoffee' | 'crypto' | 'bank' | 'patreon' | 'other' | 'steam' | 'revolut' | 'discord' | 'ko-fi';
    url: string;
    label?: string;
    iconbootstrap?: string;
}

interface DownloadEntry {
    id: number;
    version: string;
    downloads: number;
    description: string;
    translaterPrograms: Program[];
    translater_ids: number[];
    uploaderId?: number;
    uploaderName?: string;
    demo?: boolean;
    uploadDate?: string;
    size?: number; // Changed from string to number
    downloadsCount?: number;
    marked_for_deletion: boolean;
    deletion_reason: string;
    pictures?: { url: string }[];
    bad?: boolean;
    file_attached?: boolean;
    is_bad?: boolean; // Added to match API response
    uploader_is_anonymous?: boolean;
}

const DOWNLOADS_PER_PAGE = 5;

// Segédfüggvények a GameDownloadsTab.tsx-ből
const getSupportLinkIcon = (type: SupportLink['type']) => {
    switch (type) {
        case 'paypal': return <i className="bi bi-paypal"></i>;
        case 'buymeacoffee': return <i className="bi bi-cup-hot"></i>; // nincs buymeacoffee, kávés ikon helyette
        case 'crypto': return <i className="bi bi-currency-bitcoin"></i>;
        case 'bank': return <i className="bi bi-bank"></i>;
        case 'patreon': return <i className="bi bi-heart"></i>; // nincs patreon, szív ikon helyette
        case 'steam': return <i className="bi bi-steam"></i>;
        case 'ko-fi': return <i className="bi bi-cup-hot"></i>;
        case 'revolut': return <i className="bi bi-credit-card"></i>; // nincs revolut, kártya ikon helyette
        case 'discord': return <i className="bi bi-discord"></i>;
        case 'other': return <i className="bi bi-credit-card-2-front"></i>;
        default: return null;
    }
};

const getSupportLinkDisplayName = (link: SupportLink) => {
    if (link.type === 'other' && link.label) return link.label;
    switch (link.type) {
        case 'paypal': return 'PayPal';
        case 'buymeacoffee': return 'Buy Me a Coffee';
        case 'crypto': return 'Kripto';
        case 'bank': return 'Banki átutalás';
        case 'patreon': return 'Patreon';
        case 'steam': return 'Steam';
        case 'ko-fi': return 'Ko-fi';
        case 'revolut': return 'Revolut';
        default: return 'Támogatás';
    }
};

const getProgramBadgeClass = (programName: string) => {
    switch (programName.toLowerCase()) {
        case 'deepl': return "bg-blue-900/30 text-blue-300 border-blue-700 hover:bg-primary/20";
        case 'gemini': return "bg-purple-900/30 text-purple-300 border-purple-700 hover:bg-primary/20";
        case 'google': return "bg-yellow-900/30 text-yellow-300 border-yellow-700 hover:bg-primary/20";
        case 'custom': return "bg-green-900/30 text-green-300 border-green-700 hover:bg-primary/20";
        default: return "bg-gray-700 text-gray-300 border-gray-600 hover:bg-primary/20";
    }
};

const GameDownloadsTab2: React.FC<{ gameId: number, gameTitle: string }> = ({ gameId, gameTitle }) => {
    // Auth context
    const { currentUser, isAdmin, isTranslator, isRealModerator, isLoggedIn } = useAuth();
    const { registeredOnly } = useDownloadSettings();
    const blockGuestDownloads = !isLoggedIn && registeredOnly;
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
    // API-alapú állapotok
    const [downloads, setDownloads] = useState<DownloadEntry[]>([]);
    const [translators, setTranslators] = useState<{ [id: number]: Translator }>({});
    const [loading, setLoading] = useState(true);
    const [deletion_reason, setDeletionReasons] = useState<{ [id: number]: string }>({});
    const [isSubscribed, setIsSubscribed] = useState(false);

    const audioRef = useRef<HTMLAudioElement | null>(new Audio('/3.mp3')); // Play /3.mp3
    const [isPlayingAudio, setIsPlayingAudio] = useState(false);

    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            const handleAudioEnd = () => setIsPlayingAudio(false);
            const handleAudioError = () => {
                setIsPlayingAudio(false);
                showError("Hiba történt a hangfájl lejátszása során.");
            };

            audio.addEventListener('ended', handleAudioEnd);
            audio.addEventListener('error', handleAudioError);
            audio.loop = false;

            return () => {
                audio.pause();
                audio.currentTime = 0;
                audio.removeEventListener('ended', handleAudioEnd);
                audio.removeEventListener('error', handleAudioError);
            };
        }
    }, []);

    const handlePlayAudio = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlayingAudio) {
            audio.pause();
            audio.currentTime = 0;
            setIsPlayingAudio(false);
        } else {
            audio.play().catch(error => {
                console.warn("Audio play was prevented.", error);
                showError("A hang lejátszása nem indítható el automatikusan.");
            });
            setIsPlayingAudio(true);
        }
    };

    // Szerkesztési állapot
    const [editingDownloadId, setEditingDownloadId] = useState<number | null>(null);

    // Teleportálás modal állapot
    const [teleportModalOpen, setTeleportModalOpen] = useState(false);
    const [selectedUploadForTeleport, setSelectedUploadForTeleport] = useState<{
        id: number;
        gameId: number;
    } | null>(null);

    // Komponens-specifikus állapotok
    const [currentPage, setCurrentPage] = useState(1);
    const [showUpdatePanel, setShowUpdatePanel] = useState(false);
    const [openUpdateRequest, setOpenUpdateRequest] = useState<GameRequestSummary | null>(null);
    const canRequestUpdate = isLoggedIn && !!currentUser?.name;

    const loadOpenUpdateRequest = useCallback(async () => {
        if (!canRequestUpdate) return;
        try {
            const response = await fetch(`/api/game_requests?game_id=${gameId}`, { headers: { Accept: 'application/json' } });
            if (!response.ok) return;
            const requests: GameRequestSummary[] = await response.json();
            setOpenUpdateRequest(requests.find(r => r.request_type === 'update_request' && !r.completed) ?? null);
        } catch {
            setOpenUpdateRequest(null);
        }
    }, [canRequestUpdate, gameId]);

    useEffect(() => {
        loadOpenUpdateRequest();
    }, [loadOpenUpdateRequest]);
    // Állapot a képgaléria modalhoz
    const [galleryState, setGalleryState] = useState<{ isOpen: boolean; images: string[]; title: string; gameTitle: string }>({
        isOpen: false, images: [], title: '', gameTitle: ''
    });
    // Expandált leírások státusza
    const [expandedDescriptions, setExpandedDescriptions] = useState<Set<number>>(new Set());
    // Levágott leírások vizuális ellenőrzése
    const [truncatedDescriptions, setTruncatedDescriptions] = useState<{ [key: number]: boolean }>({});

    // Dinamikus truncation ellenőrzés ResizeObserver-rel
    useEffect(() => {
        const checkTruncation = () => {
            const truncated: { [key: number]: boolean } = {};
            downloads.forEach((dl) => {
                const element = document.getElementById(`description-${dl.id}`);
                if (element) {
                    // Ellenőrizzük, hogy a szöveg valóban levágódott-e
                    truncated[dl.id] = element.scrollHeight > element.clientHeight;
                }
            });
            setTruncatedDescriptions(truncated);
        };

        // Kis késleltetés, hogy a DOM render befejezze
        const timeout = setTimeout(checkTruncation, 150);

        // ResizeObserver a viewport változásra
        const resizeObserver = new ResizeObserver(() => {
            checkTruncation();
        });

        // Window resize event
        const handleResize = () => {
            checkTruncation();
        };

        // Figyeljük az összes description elemet
        downloads.forEach((dl) => {
            const element = document.getElementById(`description-${dl.id}`);
            if (element) {
                resizeObserver.observe(element);
            }
        });

        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(timeout);
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, [downloads]);

    // Adatbetöltés
    useEffect(() => {
        const fetchDownloads = async () => {
            try {
                const res = await fetch(`/api/downloads?game_id=${gameId}`);
                if (!res.ok) throw new Error("Letöltések betöltése sikertelen.");
                const data = await res.json();
                setDownloads(data);
                const translatorIds = [...new Set<number>(data.flatMap((dl: DownloadEntry) => dl.translater_ids))];
                fetchTranslators(translatorIds);
            } catch (err) {
                console.error(err);
                showError("Nem sikerült letölteni a letöltéseket.");
            } finally {
                setLoading(false);
            }
        };

        const fetchTranslators = async (ids: number[]) => {
            const fetched: { [id: number]: Translator } = {};
            await Promise.all(ids.map(async (id) => {
                try {
                    const res = await fetch(`/api/translators/${id}`);
                    if (!res.ok) throw new Error(`Translator ${id} fetch failed`);
                    const translator = await res.json();
                    fetched[id] = translator;
                } catch (err) {
                    console.warn(`Nem sikerült betölteni a fordítót: ${id}`);
                }
            }));
            setTranslators(fetched);
        };

        const fetchSubscriptionStatus = async () => {
            if (!currentUser) return;

            try {
                const res = await fetch(`/api/v1/games/${gameId}/game_download_subscriptions`, {
                    headers: {
                        "X-CSRF-Token": token,
                        "Content-Type": "application/json"
                    }
                });
                if (res.ok) {
                    const data = await res.json();
                    setIsSubscribed(data.subscribed);
                }
            } catch (error) {
                console.error("Failed to fetch subscription status", error);
            }
        };

        fetchDownloads();
        fetchSubscriptionStatus();
    }, [gameId, currentUser, token]);

    const handleSubscriptionChange = async (checked: boolean) => {
        if (!currentUser) {
            showError("A feliratkozáshoz be kell jelentkezned!");
            return;
        }

        try {
            const method = checked ? 'POST' : 'DELETE';
            const res = await fetch(`/api/v1/games/${gameId}/game_download_subscriptions`, {
                method: method,
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json"
                }
            });

            if (res.ok) {
                setIsSubscribed(checked);
                showSuccess(checked ? "Sikeresen feliratkoztál az értesítésekre!" : "Sikeresen leiratkoztál az értesítésekről.");
            } else {
                throw new Error("A művelet sikertelen.");
            }
        } catch (error) {
            console.error("Failed to update subscription", error);
            showError("Hiba történt a feliratkozás frissítése során.");
        }
    };


    // Lapozáshoz rendezés
    const sortedDownloads = useMemo(() => {
        const sorted = [...downloads].sort((a, b) => {
            if (!a.uploadDate || !b.uploadDate) return 0;
            const dateA = new Date(a.uploadDate.replace(/\./g, '-'));
            const dateB = new Date(b.uploadDate.replace(/\./g, '-'));
            return dateB.getTime() - dateA.getTime();
        });

        if (isAdmin) {
            // Admin: normális feltöltések először, aztán az elrejtett/törölt feltöltések
            const normalDownloads = sorted.filter(dl => !dl.bad && !dl.is_bad);
            const deletedDownloads = sorted.filter(dl => dl.bad || dl.is_bad);
            return [...normalDownloads, ...deletedDownloads];
        }
        return sorted;
    }, [downloads, isAdmin]);

    // Pagináció
    const visibleDownloads = useMemo(() => {
        if (isAdmin) {
            return sortedDownloads;
        }
        return sortedDownloads.filter(dl => !dl.bad && !dl.is_bad);
    }, [sortedDownloads, isAdmin]);

    const totalPages = Math.ceil(visibleDownloads.length / DOWNLOADS_PER_PAGE);
    const paginatedDownloads = useMemo(() => {
        const startIndex = (currentPage - 1) * DOWNLOADS_PER_PAGE;
        const endIndex = startIndex + DOWNLOADS_PER_PAGE;
        return visibleDownloads.slice(startIndex, endIndex);
    }, [visibleDownloads, currentPage]);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Mentés, törlés és egyéb kezelőfüggvények
    const handleSaveEdit = async (updatedDownload: DownloadEntry) => {
        const original = downloads.find(d => d.id === updatedDownload.id);
        const merged = { ...original, ...updatedDownload };
        setDownloads(prev => prev.map(d => d.id === merged.id ? merged : d));
        setEditingDownloadId(null);
        showSuccess('Letöltés sikeresen frissítve!');
    };

    const handleHide = async (id: number) => {
        if (!window.confirm("Biztosan el szeretnéd rejteni ezt a feltöltést? A normál felhasználók számára nem lesz látható.")) {
            return;
        }
        try {
            const res = await fetch(`/api/uploads/${id}`, { // This is the "hide" action
                method: "DELETE",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json"
                }
            });
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.error || "A megjelölés sikertelen.");
            }
            // Frissítjük a letöltés állapotát ahelyett, hogy törölnénk
            setDownloads(prev => prev.map(dl => dl.id === id ? { ...dl, is_bad: true, bad: true } : dl));
            showSuccess(`A feltöltés sikeresen elrejtve!`);
        } catch (err) {
            showError(err instanceof Error ? err.message : "Hiba történt a törlés során.");
        }
    };

    const handlePermanentDelete = async (id: number) => {
        if (!window.confirm("Biztosan VÉGLEG törölni szeretnéd ezt a feltöltést? Ez a művelet nem vonható vissza!")) {
            return;
        }
        try {
            const res = await fetch(`/api/uploads/${id}/permanently`, {
                method: "DELETE",
                headers: { "X-CSRF-Token": token, "Content-Type": "application/json" }
            });
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.error || "A végleges törlés sikertelen.");
            }
            const data = await res.json();
            setDownloads(prev => prev.filter(dl => dl.id !== id));
            showSuccess(data.message || `A feltöltés sikeresen törölve.`);
        } catch (err) {
            showError(err instanceof Error ? err.message : "Hiba történt a végleges törlés során.");
        }
    };

    const handlePurgeFile = async (id: number) => {
        if (!window.confirm("Biztosan törölni szeretnéd a fájlt és a képeket ehhez a feltöltéshez? Az adatlap megmarad, csak a letölthető fájl és a képek törlődnek.")) {
            return;
        }
        try {
            const res = await fetch(`/api/uploads/${id}/purge_file`, {
                method: "DELETE",
                headers: { "X-CSRF-Token": token, "Content-Type": "application/json" }
            });
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.error || "A fájl törlése sikertelen.");
            }
            const data = await res.json();
            setDownloads(prev => prev.map(dl => dl.id === id ? { ...dl, is_bad: true, bad: true, size: undefined, pictures: [] } : dl));
            showSuccess(data.message || `A fájl sikeresen törölve.`);
        } catch (err) {
            showError(err instanceof Error ? err.message : "Hiba történt a fájl törlése során.");
        }
    };

    const handleRestore = async (id: number) => {
        if (!window.confirm("Biztosan vissza szeretnéd állítani ezt a letöltést? Újra látható lesz a felhasználók számára.")) {
            return;
        }
        try {
            const res = await fetch(`/api/uploads/${id}/restore`, {
                method: "PATCH",
                headers: { "X-CSRF-Token": token, "Content-Type": "application/json" }
            });
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.error || "A visszaállítás sikertelen.");
            }
            setDownloads(prev => prev.map(dl => dl.id === id ? { ...dl, is_bad: false, bad: false } : dl));
            showSuccess(`A feltöltés sikeresen visszaállítva!`);
        } catch (err) {
            showError(err instanceof Error ? err.message : "Hiba történt a visszaállítás során.");
        }
    };

    const handleMarkForDeletion = async (id: number) => {
        try {
            const download = downloads.find(d => d.id === id);
            if (!download) return;

            const reason = deletion_reason[id] || "";
            if (!download.marked_for_deletion && !reason.trim()) {
                showError("A törlésre jelöléshez kötelező indokot megadni!");
                return;
            }

            const res = await fetch(`/api/deleted/${id}/mark`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ reason })
            });

            if (!res.ok) throw new Error("Jelölés sikertelen.");
            const data = await res.json(); // Feltételezzük, hogy a válasz tartalmazza a frissített letöltés objektumot vagy legalább az új állapotot

            // A `downloads` állapot frissítése a kapott válasszal
            setDownloads(prevDownloads =>
                prevDownloads.map(dl =>
                    dl.id === id
                        ? { ...dl, marked_for_deletion: data.marked_for_deletion, deletion_reason: reason }
                        : dl
                )
            );

            // A külön `markedForDeletion` állapot kezelésére már nincs szükség,
            // így a `setMarkedForDeletion` sort eltávolíthatjuk.

            showSuccess(data.message);
        } catch (err) {
            console.error(err);
            showError("Nem sikerült frissíteni a jelölést.");
        }
    };

    const handleDiscordReset = async (id: number) => {
        if (window.confirm("Biztosan resetelni szeretnéd a Discord botot ehhez a letöltéshez?")) {
            try {
                const res = await fetch(`/api/botreset/${id}`, {
                    method: "POST",
                    headers: { "X-CSRF-Token": token }
                });
                if (!res.ok) throw new Error("Hiba történt a Discord reset közben.");
                const data = await res.json();
                showSuccess(data.message);
            } catch (err) {
                console.error(err);
                showError("Nem sikerült resetelni a Discord botot.");
            }
        }
    };

    if (loading) {
        return (
            <Card className="bg-gray-800 text-secondary-foreground border-border shadow-md">
                <CardHeader>
                    <CardTitle className="text-xl font-bold text-primary flex items-center gap-2">
                        <Download /> Letöltések
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-muted-foreground">Betöltés folyamatban...</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="bg-gray-800 text-secondary-foreground border-border shadow-md">
            <CardHeader>
                <CardTitle className="text-xl font-bold text-primary flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <Download /> Letöltések
                        <Button
                            onClick={handlePlayAudio}
                            variant="secondary"
                            size="icon"
                            className="ml-2 h-8 w-8 animate-pulse"
                            title="Tudni valók (hang)"
                        >
                            {isPlayingAudio ? <Volume2 size={16} /> : <HelpCircle size={16} />}
                        </Button>
                        {canRequestUpdate && (
                            <Button
                                onClick={() => setShowUpdatePanel(prev => !prev)}
                                variant="secondary"
                                size="sm"
                                className="ml-2"
                            >
                                <Plus size={16} className="mr-1" /> Új verzió kérése
                            </Button>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center space-x-2 opacity-60 min-w-0">
                        <Checkbox id="notification-subscribe" checked={isSubscribed} onCheckedChange={handleSubscriptionChange} disabled={true} />
                        <label
                            htmlFor="notification-subscribe"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                            Értesítést kérek az új feltöltésekről <span className="text-yellow-400 font-semibold">(átmenetileg nem működik)</span>
                        </label>
                    </div>

                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {openUpdateRequest && (
                    <div className="rounded-md border border-border bg-gray-900/40 p-3 text-sm">
                        Ehhez a játékhoz már van nyitott frissítés kérés:{' '}
                        <Link to={`/keres/${openUpdateRequest.id}`} className="font-semibold text-primary hover:underline">
                            #{openUpdateRequest.id}{openUpdateRequest.version ? ` (${openUpdateRequest.version})` : ''} megtekintése →
                        </Link>
                    </div>
                )}
                {showUpdatePanel && (
                    <GameUpdateRequestPanel
                        gameId={gameId}
                        gameTitle={gameTitle}
                        onCancel={() => setShowUpdatePanel(false)}
                        onCreated={() => {
                            setShowUpdatePanel(false);
                            loadOpenUpdateRequest();
                        }}
                    />
                )}
                {paginatedDownloads.map((dl, index) => (
                    editingDownloadId === dl.id ? (
                        <EditDownloadForm
                            key={dl.id}
                            downloadId={dl.id}
                            uploaderId={dl.uploaderId}
                            uploadDate={dl.uploadDate}
                            gameTitle={gameTitle}
                            onSave={handleSaveEdit}
                            onCancel={() => setEditingDownloadId(null)}
                        />
                    ) : (
                        <Card key={dl.id} className={cn(
                            "bg-card text-card-foreground border-border",
                            dl.demo && "border-destructive ring-2 ring-destructive/50",
                            dl.marked_for_deletion && "!border-yellow-500 !ring-yellow-500/50 !ring-2",
                            (dl.bad || dl.is_bad) && "bg-orange-900/50 border-orange-700"
                        )}>
                            <CardHeader className="p-4 pb-2">
                                <div className="flex-grow">
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                        <h3 className="text-lg font-semibold text-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
                                            <span className="text-primary">Verzió:</span> {dl.version}
                                            {index === 0 && currentPage === 1 && (
                                                <Badge variant="default" className="ml-2 bg-primary text-primary-foreground border border-primary/50">
                                                    Legújabb
                                                </Badge>
                                            )}
                                            {dl.demo && <Badge variant="destructive" className="text-sm">DEMO</Badge>}
                                            {(dl.bad || dl.is_bad) && <Badge variant="destructive" className="bg-orange-600 text-white text-sm">Rossz</Badge>}
                                        </h3>
                                        <div className="flex flex-wrap gap-2 items-center">
                                            <Code size={16} className="text-purple-400" />
                                            {dl.translaterPrograms?.map(program => (
                                                <Badge key={program.id} className={cn("border", getProgramBadgeClass(program.program_name))}>
                                                    {program.program_name}
                                                </Badge>
                                            ))}
                                        </div>
                                        <div className="flex flex-col items-start gap-2 text-foreground sm:flex-row sm:items-center sm:gap-4">
                                            <div className="flex items-center gap-1"><Download size={14} /> {dl.downloadsCount?.toLocaleString() ?? 0} letöltés</div>
                                            {dl.size !== undefined && dl.size > 0 && <div className="flex items-center gap-1"><HardDrive size={14} /> {formatBytes(dl.size)}</div>}
                                            <div className="flex items-center gap-1"><Upload size={14} /> {dl.uploadDate}</div>
                                        </div>
                                    </div>

                                </div>
                            </CardHeader>
                            <CardContent className="p-4 pt-2">

                                <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-4 gap-y-2 pt-2 pb-2 border-t border-b border-border min-w-0">
                                    {dl.description && dl.description.length > 0 && (
                                        <>
                                            <h4 className="text-base font-semibold text-primary text-left sm:text-right">Leírás:</h4>
                                            <div className="w-full">
                                                <div
                                                    id={`description-${dl.id}`}
                                                    className={cn(
                                                        "text-sm text-foreground break-words w-full overflow-hidden transition-all",
                                                        !expandedDescriptions.has(dl.id) && truncatedDescriptions[dl.id] && "line-clamp-3"
                                                    )}
                                                >
                                                    <p
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                DOMPurify.sanitize(dl.description)
                                                                    .replace(/\n\n+/g, '<br /><br />')
                                                                    .replace(/\n/g, '<br />')
                                                                    .replace(
                                                                        /(?<!href=")(https?:\/\/[^\s<]+)/g,
                                                                        '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-500 font-bold">&lt;LINK&gt;</a>'
                                                                    )
                                                        }}
                                                    />
                                                </div>
                                                {truncatedDescriptions[dl.id] && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            setExpandedDescriptions(prev => {
                                                                const newSet = new Set(prev);
                                                                if (newSet.has(dl.id)) {
                                                                    newSet.delete(dl.id);
                                                                } else {
                                                                    newSet.add(dl.id);
                                                                }
                                                                return newSet;
                                                            });
                                                        }}
                                                        className="mt-2 p-0 h-auto text-blue-500 hover:text-blue-400 hover:bg-transparent"
                                                    >
                                                        {expandedDescriptions.has(dl.id) ? '▲ Bezár' : '▼ Továbbá olvas'}
                                                    </Button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    <h4 className="text-base font-semibold text-primary self-start sm:self-center text-left sm:text-right">{dl.translater_ids.length === 1 ? 'Készítő' : 'Készítők'}:<br />Támogatás:</h4>
                                    <div className="flex flex-wrap gap-2 w-full overflow-hidden">
                                        {dl.uploader_is_anonymous ? (
                                            <div className="flex items-center gap-3 p-2 ">
                                                <div className="flex-shrink-0">
                                                    <Avatar className="w-12 h-12">
                                                        <AvatarFallback className="bg-secondary text-white text-lg">?</AvatarFallback>
                                                    </Avatar>
                                                </div>
                                                <div className="flex flex-col justify-center flex-grow">
                                                    <p className="text-sm font-semibold text-foreground self-center">Névtelen</p>
                                                </div>
                                            </div>
                                        ) : (
                                            dl.translater_ids.map((id) => {
                                                const translator = translators[id];
                                                if (!translator) return null;
                                                const hasSupport = translator.supportLinks && translator.supportLinks.length > 0;
                                                return (
                                                    <div key={id} className="flex items-center gap-3 p-2 ">
                                                        <div className="flex-shrink-0">
                                                            <a href={`/translator/${translator.slug}`}>
                                                                <Avatar className="w-12 h-12">
                                                                    <AvatarImage src={translator.avatar} title={translator.name || "User"} className="object-cover" />
                                                                    <AvatarFallback className="bg-secondary text-white text-lg">{String(translator.name).charAt(0).toUpperCase()}</AvatarFallback>
                                                                </Avatar>
                                                            </a>
                                                        </div>
                                                        <div className={`flex flex-col ${hasSupport ? "justify-start" : "justify-center"} flex-grow`}>
                                                            <a href={`/translator/${translator.slug}`}>
                                                                <p className="text-sm font-semibold text-foreground self-center">{translator.name}</p>
                                                            </a>
                                                            {hasSupport && (
                                                                <div className="flex flex-wrap gap-1 mt-1">
                                                                    {translator.supportLinks?.map((link, linkIndex) => (
                                                                        <Button key={linkIndex} variant="outline" size="icon" className="h-6 w-6" asChild>
                                                                            <a href={link.url} target="_blank" rel="noopener noreferrer" title={getSupportLinkDisplayName(link)}>
                                                                                {link.iconbootstrap ? <span dangerouslySetInnerHTML={{ __html: link.iconbootstrap }} /> : getSupportLinkIcon(link.type)}
                                                                            </a>
                                                                        </Button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-center items-center mt-2 gap-2">
                                    {blockGuestDownloads ? (
                                        <Button disabled className="bg-primary text-primary-foreground">
                                            <Download size={16} className="mr-2" />Letöltés
                                        </Button>
                                    ) : (
                                        <Button asChild className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground">
                                            <Link to={`/download/${dl.id}`}>
                                                <Download size={16} className="mr-2" />Letöltés
                                            </Link>
                                        </Button>
                                    )}
                                    {(dl.pictures?.length ?? 0) > 0 && (
                                        <Button
                                            variant="outline"
                                            onClick={() => setGalleryState({
                                                isOpen: true,
                                                images: dl.pictures?.map(p => p.url) ?? [],
                                                title: `Képek - ${dl.version}`,
                                                gameTitle: gameTitle
                                            })}
                                        >
                                            Képek ({dl.pictures?.length ?? 0})
                                        </Button>
                                    )}
                                </div>
                                {blockGuestDownloads && (
                                    <p className="text-xs text-center text-muted-foreground mt-2">A letöltéshez regisztráció és bejelentkezés szükséges.</p>
                                )}
                            </CardContent>
                            {(isTranslator || isAdmin || isRealModerator) && (
                                <CardFooter className="p-4 pt-4 border-t border-border flex flex-wrap justify-between items-center gap-4">
                                    <div>
                                        {dl.marked_for_deletion && (
                                            <Badge variant="destructive" className="bg-yellow-600/80 text-white">Törlésre jelölve</Badge>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {(isAdmin || isRealModerator || (isTranslator && currentUser?.id === dl.uploaderId)) && (
                                            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                                                {!dl.marked_for_deletion && (
                                                    <Input
                                                        placeholder="Törlés oka..."
                                                        value={deletion_reason[dl.id] || ""}
                                                        onChange={(e) => setDeletionReasons(prev => ({ ...prev, [dl.id]: e.target.value }))}
                                                        className="h-8 w-full text-sm sm:w-48"
                                                    />
                                                )}
                                                <Button variant="outline" size="sm" onClick={() => handleMarkForDeletion(dl.id)} className="w-full sm:w-auto">
                                                    <AlertTriangle size={14} className="mr-1" /> {dl.marked_for_deletion ? 'Jelölés visszavonása' : 'Törlésre jelöl'}
                                                </Button>
                                            </div>
                                        )}
                                        {(isAdmin || (isTranslator && currentUser?.id === dl.uploaderId)) && (
                                            <Button variant="outline" size="sm" onClick={() => handleDiscordReset(dl.id)}>
                                                <Bot size={14} className="mr-1" /> Discord Bot Reset
                                            </Button>
                                        )}
                                        {(isAdmin || (isTranslator && currentUser?.id === dl.uploaderId)) && (
                                            <Button variant="outline" size="sm" onClick={() => setEditingDownloadId(dl.id)} className="bg-muted text-muted-foreground border-border hover:bg-secondary hover:text-secondary-foreground">
                                                <Edit size={14} />
                                            </Button>
                                        )}
                                        {isAdmin && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedUploadForTeleport({ id: dl.id, gameId: gameId });
                                                    setTeleportModalOpen(true);
                                                }}
                                                className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                                                title="Ez a feltöltés áthelyezése másik játékra"
                                            >
                                                <Truck size={14} className="mr-1" /> Szállítsd el
                                            </Button>
                                        )}
                                        {isAdmin && (
                                            <>
                                                {dl.is_bad ? (
                                                    dl.file_attached && (
                                                        <Button variant="outline" size="sm" onClick={() => handleRestore(dl.id)} className="border-primary text-primary hover:bg-primary/10 hover:text-primary/90">
                                                            <Trash2 size={14} className="mr-1" /> Visszaállítás
                                                        </Button>
                                                    )
                                                ) : (
                                                    <Button variant="destructive" size="sm" onClick={() => handleHide(dl.id)}>
                                                        <Trash2 size={14} className="mr-1" /> Elrejtés
                                                    </Button>
                                                )}
                                                {dl.file_attached && <Button variant="destructive" size="sm" onClick={() => handlePurgeFile(dl.id)}>
                                                    <Trash2 size={14} className="mr-1" /> Fájl törlés
                                                </Button>}
                                                <Button variant="destructive" size="sm" onClick={() => handlePermanentDelete(dl.id)}>
                                                    <Trash2 size={14} className="mr-1" /> Végleges törlés
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </CardFooter>
                            )}
                        </Card>
                    )
                ))}

                {totalPages > 1 && (
                    <Pagination className="mt-6">
                        <PaginationContent className="flex-wrap justify-center">
                            <PaginationItem>
                                <PaginationPrevious onClick={() => handlePageChange(currentPage - 1)} className={`${currentPage === 1 ? "pointer-events-none opacity-50" : ""} hover:border-primary hover:bg-muted hover:text-muted-foreground transition-colors duration-200 cursor-pointer`} />
                            </PaginationItem>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <PaginationItem key={page}>
                                    <PaginationLink onClick={() => handlePageChange(page)} isActive={currentPage === page} className={`${currentPage === page ? "bg-primary text-primary-foreground" : ""} hover:border-primary hover:bg-muted hover:text-muted-foreground transition-colors duration-200 cursor-pointer h-8 w-8 sm:h-10 sm:w-10 text-xs sm:text-sm p-0`}>
                                        {page}
                                    </PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem>
                                <PaginationNext onClick={() => handlePageChange(currentPage + 1)} className={`${currentPage === totalPages ? "pointer-events-none opacity-50" : ""} hover:border-primary hover:bg-muted hover:text-muted-foreground transition-colors duration-200 cursor-pointer`} />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                )}
            </CardContent>
            {/* A galéria modal, amit a gomb vezérel */}
            <ImageGallery
                isOpen={galleryState.isOpen}
                onOpenChange={(isOpen) => setGalleryState(prev => ({ ...prev, isOpen }))}
                images={galleryState.images}
                title={galleryState.title}
            />

            {/* Upload Teleportálás Modal */}
            {selectedUploadForTeleport && (
                <UploadTeleportModal
                    isOpen={teleportModalOpen}
                    onClose={() => {
                        setTeleportModalOpen(false);
                        setSelectedUploadForTeleport(null);
                    }}
                    uploadId={selectedUploadForTeleport.id}
                    currentGameId={selectedUploadForTeleport.gameId}
                    currentGameName={gameTitle}
                />
            )}
        </Card>
    );
};

export default GameDownloadsTab2;